import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, Lock, Mail, RefreshCcw } from 'lucide-react';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const segment = searchParams.get('segment') === 'business' ? 'business' : 'personal';
  const segmentLabel = segment === 'business' ? 'Business' : 'Pessoal';

  const helperText = useMemo(() => ({
    email: 'Confirme o e-mail do cadastro para receber o código de recuperação.',
    code: 'Digite o código enviado para o seu e-mail. Você pode reenviar ou alterar o endereço.',
    password: 'Defina sua nova senha para voltar a acessar o sistema.',
  }), []);

  const requestCode = async () => {
    if (!email.trim()) {
      toast.error('Informe o e-mail cadastrado.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível enviar o código agora.');
      }
      toast.success(data.message || 'Código enviado.');
      setStep('code');
      setCode('');
      setResetToken('');
    } catch (error) {
      toast.error(error.message || 'Erro ao solicitar código.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) {
      toast.error('Informe o código recebido por e-mail.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível validar o código.');
      }
      setResetToken(data.reset_token || '');
      setStep('password');
      toast.success(data.message || 'Código validado.');
    } catch (error) {
      toast.error(error.message || 'Erro ao validar o código.');
    } finally {
      setLoading(false);
    }
  };

  const saveNewPassword = async () => {
    if (!password || !confirmPassword) {
      toast.error('Preencha a nova senha e a confirmação.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          reset_token: resetToken,
          password,
          confirm_password: confirmPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível cadastrar a nova senha.');
      }
      toast.success(data.message || 'Senha cadastrada com sucesso.');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Erro ao cadastrar nova senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="p-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="text-zinc-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para login
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center">
                <BrandIcon className="w-5 h-5 text-white" />
              </div>
              <div className="text-left min-w-0">
                <span className="font-heading text-lg sm:text-xl font-semibold text-zinc-900 block leading-tight">Zapt Resumo</span>
                <span className="text-[10px] font-semibold tracking-[0.18em] text-sky-700 block leading-tight">{segmentLabel.toUpperCase()}</span>
              </div>
            </div>
            <CardTitle className="text-2xl">Recuperar senha</CardTitle>
            <CardDescription>{helperText[step]}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            {step === 'email' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">E-mail cadastrado</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button type="button" className="w-full rounded-full" onClick={requestCode} disabled={loading}>
                  {loading ? 'Enviando código...' : 'Receber código por e-mail'}
                </Button>
              </div>
            )}

            {step === 'code' && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600">
                  Código enviado para <span className="font-medium text-zinc-900">{email}</span>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reset-code">Código de verificação</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="reset-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="pl-10 tracking-[0.4em]"
                    />
                  </div>
                </div>

                <Button type="button" className="w-full rounded-full" onClick={verifyCode} disabled={loading}>
                  {loading ? 'Validando código...' : 'Validar código'}
                </Button>

                <div className="grid gap-2 sm:grid-cols-2">
                  <Button type="button" variant="outline" className="rounded-full" onClick={requestCode} disabled={loading}>
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Reenviar código
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => {
                      setStep('email');
                      setCode('');
                      setResetToken('');
                    }}
                    disabled={loading}
                  >
                    Alterar e-mail
                  </Button>
                </div>
              </div>
            )}

            {step === 'password' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nova senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button type="button" className="w-full rounded-full" onClick={saveNewPassword} disabled={loading}>
                  {loading ? 'Salvando senha...' : 'Salvar nova senha'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
